import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, twoFaAPI } from '../lib/api';

const AuthContext = createContext(null);

/**
 * Token storage utilities.
 * Note: For production, consider migrating to httpOnly cookies for token storage.
 * sessionStorage is used here as it clears on tab close (more secure than localStorage).
 */
const tokenStore = {
  get: () => sessionStorage.getItem('token') || localStorage.getItem('token'),
  set: (token) => { sessionStorage.setItem('token', token); localStorage.setItem('token', token); },
  remove: () => { sessionStorage.removeItem('token'); localStorage.removeItem('token'); },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await authAPI.me();
      setUser(data);
    } catch {
      tokenStore.remove();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }
    tokenStore.set(data.token);
    setUser(data.user);
    return data;
  };

  const verify2FA = async (code, tempToken) => {
    const { data } = await twoFaAPI.verify(code, tempToken);
    tokenStore.set(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (payload) => {
    const { data } = await authAPI.register(payload);
    tokenStore.set(data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch { /* ignore logout errors */ }
    tokenStore.remove();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2FA, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
